<script lang="ts" setup>
import type { DriverDto } from "@/api/users/dto/driver.dto";
import { computed, ref, watch } from "vue";
import { useError } from "@/composables/useError";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import OrderField from "@/components/inputs/OrderField.vue";

const api = new API();
const { showAPIError } = useError();
const { t } = useI18n();

const MENU_WIDTH = 1248;
const MENU_OFFSET: [number, number] = [-200, 200];

const props = defineProps<{
  user: DriverDto;
}>();

const note = ref("");
const driverNote = ref("");
const savedNote = ref("");
const savedDriverNote = ref("");
const isSaving = ref(false);
const isOpen = ref(false);

function setDefaultNotes() {
  note.value = props.user.note ?? "";
  driverNote.value = props.user.driverNote ?? "";
  savedNote.value = note.value;
  savedDriverNote.value = driverNote.value;
}

const hasNotesChanged = computed(() => {
  return note.value !== savedNote.value || driverNote.value !== savedDriverNote.value;
});

const hasNotes = computed(() => Boolean(savedNote.value || savedDriverNote.value));

async function save() {
  if (!hasNotesChanged.value || isSaving.value || props.user.id == null) {
    return;
  }

  isSaving.value = true;

  try {
    const response = await api.users.updateDriver(props.user.id, {
      note: note.value,
      driverNote: driverNote.value,
    });

    if (response.errorData) {
      showAPIError(response, t("drivers.update-note-failed"));
      return;
    }

    savedNote.value = note.value;
    savedDriverNote.value = driverNote.value;
  } finally {
    isSaving.value = false;
  }
}

watch(() => props.user, setDefaultNotes, { immediate: true });
</script>

<template>
  <v-menu v-model="isOpen" :close-on-content-click="false" :offset="MENU_OFFSET" :width="MENU_WIDTH">
    <template #activator="{ props: menuProps }">
      <v-btn
        :class="hasNotes ? 'text-primary' : 'text-grey-darken-1'"
        class="quote-btn"
        data-qa="driver-notes-button"
        size="small"
        v-bind="menuProps"
        variant="flat"
      >
        <v-icon size="small">mdi-format-quote-close</v-icon>
      </v-btn>
    </template>

    <v-card class="elevation-12 border position-relative">
      <v-btn
        class="position-absolute"
        density="comfortable"
        icon="mdi-close"
        style="top: 8px; right: 8px"
        variant="text"
        @click="isOpen = false"
      />

      <div class="py-5 px-6">
        <order-field v-model="note">
          <template #title>
            <span class="font-size-14 text-grey-darken-3">{{ t("maps.dispatcher-note") }}</span>
          </template>
        </order-field>

        <order-field v-model="driverNote" class="mt-6">
          <template #title>
            <span class="font-size-14 text-grey-darken-3">{{ t("drivers.driver-note") }}</span>
          </template>
        </order-field>
      </div>

      <v-card-actions class="d-flex justify-end pa-4">
        <v-btn :disabled="!hasNotesChanged" :loading="isSaving" color="primary" variant="flat" @click="save">{{
          t("general.save")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped>
.quote-btn {
  position: absolute;
  top: 2px;
  right: 4px;
  min-width: auto !important;
  height: auto !important;
  background-color: transparent !important;
}
</style>
